import type { BaseUser } from "@models";
import { BlockThread, InlineThread } from "@tiptap-pro/extension-comments";
import { ExportMarkdown } from "@tiptap-pro/extension-export-markdown";
import { getPreviewContentFromVersionPayload } from "@tiptap-pro/extension-snapshot";
import {
  SnapshotCompare,
  type ChangeTrackingTransform,
  type Diff,
} from "@tiptap-pro/extension-snapshot-compare";
import {
  SuggestionMark,
  SuggestionNodeAttributes,
} from "@tiptap-pro/extension-tracked-changes";
import type { TiptapCollabProvider } from "@tiptap-pro/provider";
import type { Extensions, JSONContent } from "@tiptap/core";
import { Highlight } from "@tiptap/extension-highlight";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Typography } from "@tiptap/extension-typography";
import { Selection } from "@tiptap/extensions";
import { Markdown } from "@tiptap/markdown";
import { useEditor, type Editor } from "@tiptap/react";
import { ServerAiToolkit } from "@tiptap/server-ai-toolkit";
import { StarterKit } from "@tiptap/starter-kit";
import { isEqual } from "lodash";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { TrackedChangesInputCompatibility } from "src/components/TiptapEditor/collaboration";
import { NodeBackground } from "src/tiptap/components/tiptap-extension/node-background-extension";
import {
  ConditionalNode,
  ConditionalTableCell,
  ConditionalTableRow,
  ContextNode,
  DataKeyNode,
  DidNode,
  HandlebarsNode,
  SlashCommand,
} from "src/tiptap/components/tiptap-node/dynamic-nodes";
import { HorizontalRule } from "src/tiptap/components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension";
import { createImageExtension } from "src/tiptap/components/tiptap-node/image-upload-node";
import { normalizeLegacyImageContent } from "src/tiptap/components/tiptap-node/image-upload-node/normalize-legacy-image-content";
import { createTableExtensions } from "src/tiptap/components/tiptap-node/table-node";
import * as Y from "yjs";
import { getReadyEditor } from "./editor-readiness";
import type { VersionComparisonDynamicContentResolver } from "./version-comparison/dynamic-version-comparison";
import {
  getVersionComparisonDecorationDiffType,
  normalizeVersionComparisonDiffForRendering,
  shouldRenderVersionComparisonDiff,
} from "./version-comparison/suggestion-version-comparison";
import { mapNativeTableDeletionDiffToDecorations } from "./version-comparison/table-version-comparison";
import { buildDynamicContentVersionComparison } from "./version-comparison/version-comparison";
import { sanitizeNativeVersionComparisonDiffs } from "./version-comparison/version-comparison-diffs";
import {
  getVersionComparisonAuthorId,
  getVersionComparisonAuthorsForIds,
  getVersionComparisonColorForUserId,
  getVersionComparisonDiffBackgroundColor,
  getVersionComparisonDiffDecorationAttributes,
  getVersionComparisonRemovedColor,
  shouldShowVersionComparisonDiffInLegend,
  UNKNOWN_VERSION_COMPARISON_USER_ID,
  type SimpleEditorVersionComparisonAuthor,
  type SimpleEditorVersionComparisonState,
} from "./version-comparison/version-comparison-presentation";
import {
  configureSnapshotVersioning,
  getVersionTitle,
  getVersionViewingMessage,
  VersionCards,
  type SimpleEditorSnapshotVersion,
  type SimpleEditorVersionCardAuthorByVersion,
  type SimpleEditorVersionCardRestorableByVersion,
  type SimpleEditorVersionCardStatus,
  type SimpleEditorVersionCardStatusByVersion,
  type SimpleEditorVersionCardTitleByVersion,
  type VersionCardFilter,
} from "./version-history";

type SimpleEditorPreviewActionBase = {
  ariaLabel: string;
  isLoading?: boolean;
  label: string;
  onClick: () => void;
};

export type SimpleEditorPreviewAction = SimpleEditorPreviewActionBase &
  (
    | {
        kind: "close";
        viewingMessage: string;
      }
    | {
        kind: "resume";
      }
  );

export type SimpleEditorComparison = {
  onExit: () => void;
  sourceTitle: string;
  sourceVersion: number;
  targetTitle: string | null;
  targetVersion: number | null;
};

export type SimpleEditorVersionsController = {
  clearSelectedVersion: () => void;
  compareVersion: (version: number) => void;
  comparison: SimpleEditorComparison | null;
  currentVersion: number | null;
  handleSnapshotUpdate: (nextVersions: SimpleEditorSnapshotVersion[]) => void;
  isVersionHistorySynced: boolean;
  isLoading: boolean;
  isVersionPreviewActive: boolean;
  previewAction: SimpleEditorPreviewAction | null;
  previewEditor: Editor | null;
  selectVersion: (version: number) => void;
  selectedVersion: number | null;
  versions: SimpleEditorSnapshotVersion[];
  versionCards: ReactNode | null;
  versionComparison: SimpleEditorVersionComparisonState | null;
};

type UseSimpleEditorVersionsOptions = {
  collaborationProvider: TiptapCollabProvider | null;
  getVersionCardAuthorByVersion?: (
    latestTiptapVersion: number | null
  ) => SimpleEditorVersionCardAuthorByVersion;
  getVersionCardStatusByVersion?: (
    latestTiptapVersion: number | null
  ) => SimpleEditorVersionCardStatusByVersion;
  getVersionCardRestorableByVersion?: (
    latestTiptapVersion: number | null
  ) => SimpleEditorVersionCardRestorableByVersion;
  getVersionCardTitleByVersion?: (
    latestTiptapVersion: number | null
  ) => SimpleEditorVersionCardTitleByVersion;
  isEnabled?: boolean;
  isEditable: boolean;
  onRestoreVersion?: (
    version: SimpleEditorSnapshotVersion
  ) => Promise<void> | void;
  orgUsers: BaseUser[];
  readyEditor: Editor | null;
  resolveDynamicContentForVersion?: VersionComparisonDynamicContentResolver;
  trackedChangesUserId: string;
  trackedChangesUserName: string;
  versionsRefreshKey: string;
};

const EMPTY_VERSION_CARD_STATUS_BY_VERSION = new Map<
  number,
  SimpleEditorVersionCardStatus
>();
const EMPTY_VERSION_CARD_RESTORABLE_BY_VERSION = new Map<
  number,
  boolean | undefined
>();
const EMPTY_VERSION_CARD_TITLE_BY_VERSION = new Map<number, string>();

type VersionComparisonStatus =
  | { status: "idle" }
  | {
      fromVersion: number;
      fromTitle: string;
      status: "loading";
      toTitle: string | null;
      toVersion: number | null;
    }
  | {
      comparison: SimpleEditorVersionComparisonState;
      status: "ready";
      toTitle: string | null;
      toVersion: number | null;
    };

type VersionPreviewPayload = {
  event: "version.preview";
  version: number;
  ydoc: string;
};

type VersionComparisonAuthorResolver = (
  userId?: string | null
) => SimpleEditorVersionComparisonAuthor;

type SnapshotVersionPreview = {
  // JSON drives the selected-version preview. Snapshot bytes remain the source
  // of content and authorship for every comparison.
  content: JSONContent | null;
  snapshot: Uint8Array;
};

type LatestVersionPreview = {
  content: JSONContent;
  version: number;
};

const VERSION_PREVIEW_TIMEOUT_MS = 10000;

const createVersionPreviewExtensions = ({
  collaborationProvider,
  getVersionComparisonAuthorRef,
}: {
  collaborationProvider: TiptapCollabProvider | null;
  getVersionComparisonAuthorRef: {
    current: VersionComparisonAuthorResolver;
  };
}): Extensions => [
  StarterKit.configure({
    horizontalRule: false,
    link: {
      openOnClick: false,
      enableClickSelection: true,
    },
  }),
  DataKeyNode,
  ContextNode,
  DidNode,
  ConditionalNode,
  ConditionalTableRow,
  ConditionalTableCell,
  HandlebarsNode,
  SlashCommand,
  TrackedChangesInputCompatibility,
  SuggestionMark,
  SuggestionNodeAttributes,
  // Historical snapshots can contain comment anchors, including the companion
  // threads created for tracked suggestions. Keep their schema without the
  // Comments decoration plugin, which would render every anchor as unresolved
  // when the comparison editor has no provider.
  BlockThread,
  InlineThread,
  ServerAiToolkit,
  HorizontalRule,
  TextAlign.configure({ types: ["heading", "paragraph"] }),
  TextStyle,
  TaskList,
  TaskItem.configure({ nested: true }),
  Highlight.configure({ multicolor: true }),
  NodeBackground,
  createImageExtension({ resize: false }),
  Typography,
  Superscript,
  Subscript,
  Selection,
  ...createTableExtensions(),
  Markdown,
  ExportMarkdown.configure({
    onCompleteExport: () => undefined,
  }),
  SnapshotCompare.configure({
    provider: collaborationProvider,
    mapDiffToDecorations: ({
      defaultMapDiffToDecorations,
      diff,
      editor,
      tr,
    }) => {
      const author = getVersionComparisonAuthorRef.current(
        diff.attribution.userId
      );
      const diffNode =
        diff.type === "block-update" ? tr.doc.nodeAt(diff.from) : null;
      const decorationDiffType = getVersionComparisonDecorationDiffType(diff);
      const backgroundColor = getVersionComparisonDiffBackgroundColor({
        color: author.color,
        diffType: decorationDiffType,
      });
      const decorationAttributes = getVersionComparisonDiffDecorationAttributes(
        {
          backgroundColor,
          content: diff.content,
          decorationDiffType,
          diffContentType: diff.diffContentType,
          diffType: diff.type,
          nodeTypeName: diffNode?.type.name,
          userId: author.id,
          userName: author.name,
        }
      );

      const tableDeletionDecorations = mapNativeTableDeletionDiffToDecorations({
        attributes: decorationAttributes,
        diff,
        editor,
      });

      if (tableDeletionDecorations) {
        return tableDeletionDecorations;
      }

      return defaultMapDiffToDecorations({
        diff,
        editor,
        tr,
        attributes: decorationAttributes,
      });
    },
  }),
];

export const normalizeLegacyImagePreviewContent = (
  content: JSONContent
): JSONContent => normalizeLegacyImageContent(content);

const clonePreviewContent = (content: JSONContent): JSONContent =>
  normalizeLegacyImagePreviewContent(content);

const hideDiffIfAvailable = (editor: Editor | null) => {
  const readyEditor = getReadyEditor(editor);

  if (typeof readyEditor?.commands.hideDiff !== "function") {
    return;
  }

  readyEditor.commands.hideDiff();
};

const decodeVersionPreviewUpdate = (value: string) => {
  const binary = atob(value);
  const update = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    update[index] = binary.charCodeAt(index);
  }

  return Y.convertUpdateFormatV1ToV2(update);
};

const parseVersionPreviewPayload = (
  value: string
): VersionPreviewPayload | null => {
  try {
    const payload = JSON.parse(value) as Partial<VersionPreviewPayload>;

    if (
      payload.event === "version.preview" &&
      typeof payload.version === "number" &&
      typeof payload.ydoc === "string"
    ) {
      return payload as VersionPreviewPayload;
    }
  } catch {
    return null;
  }

  return null;
};

const getVersionPreviewContentFromPayload = (
  value: string
): JSONContent | null => {
  const content = getPreviewContentFromVersionPayload(value);

  return content && typeof content === "object"
    ? (content as JSONContent)
    : null;
};

const fetchSnapshotVersionPreview = ({
  provider,
  version,
}: {
  provider: TiptapCollabProvider;
  version: number;
}): Promise<SnapshotVersionPreview> => {
  if (
    !provider.getVersions().some((candidate) => candidate.version === version)
  ) {
    return Promise.reject(new Error("Version does not exist!"));
  }

  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error("operation timed out"));
    }, VERSION_PREVIEW_TIMEOUT_MS);

    function cleanup() {
      window.clearTimeout(timeoutId);
      provider.off("stateless", handleStateless);
    }

    function handleStateless(event: { payload: string }) {
      const payload = parseVersionPreviewPayload(event.payload);

      if (!payload) {
        return;
      }

      if (payload.version !== version) {
        return;
      }

      cleanup();
      resolve({
        content: getVersionPreviewContentFromPayload(event.payload),
        snapshot: decodeVersionPreviewUpdate(payload.ydoc),
      });
    }

    provider.on("stateless", handleStateless);

    try {
      provider.sendStateless(
        JSON.stringify({
          action: "version.preview",
          version,
        })
      );
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
};

export const getHeadMatchingVersion = ({
  currentVersion,
  headContent,
  latestVersionPreview,
}: {
  currentVersion: number | null;
  headContent: JSONContent | null;
  latestVersionPreview: LatestVersionPreview | null;
}) => {
  if (
    currentVersion === null ||
    !headContent ||
    latestVersionPreview?.version !== currentVersion
  ) {
    return null;
  }

  return isEqual(latestVersionPreview.content, headContent)
    ? currentVersion
    : null;
};

const getLatestSnapshotVersion = (
  versions: SimpleEditorSnapshotVersion[]
): number | null =>
  versions.reduce<number | null>(
    (latestVersion, version) =>
      latestVersion === null || version.version > latestVersion
        ? version.version
        : latestVersion,
    null
  );

type UseSimpleEditorVersionComparisonOptions = {
  collaborationProvider: TiptapCollabProvider | null;
  latestTiptapVersion: number | null;
  getVersionTitleForDisplay: (version: SimpleEditorSnapshotVersion) => string;
  onPreviewComparedVersion: (version: number, content: JSONContent) => void;
  onSelectVersion: (version: number | null) => void;
  orgUsers: BaseUser[];
  previewEditorRef: { current: Editor | null };
  readyEditor: Editor | null;
  resolveDynamicContentForVersion?: VersionComparisonDynamicContentResolver;
  trackedChangesUserId: string;
  trackedChangesUserName: string;
};

const useSimpleEditorVersionComparison = ({
  collaborationProvider,
  latestTiptapVersion,
  getVersionTitleForDisplay,
  onPreviewComparedVersion,
  onSelectVersion,
  orgUsers,
  previewEditorRef,
  readyEditor,
  resolveDynamicContentForVersion,
  trackedChangesUserId,
  trackedChangesUserName,
}: UseSimpleEditorVersionComparisonOptions) => {
  const [versionComparisonStatus, setVersionComparisonStatus] =
    useState<VersionComparisonStatus>({ status: "idle" });
  const versionComparisonAuthorsRef = useRef<
    Map<string, SimpleEditorVersionComparisonAuthor>
  >(new Map());
  const comparisonRequestIdRef = useRef(0);

  const versionComparisonUserMap = useMemo(() => {
    const authorIds = [
      ...orgUsers.map((user) => user.id),
      trackedChangesUserId,
      UNKNOWN_VERSION_COMPARISON_USER_ID,
    ];
    const authors = getVersionComparisonAuthorsForIds({
      authorIds,
      orgUsers,
      trackedChangesUserId,
      trackedChangesUserName,
    });

    return new Map(authors.map((author) => [author.id, author]));
  }, [orgUsers, trackedChangesUserId, trackedChangesUserName]);

  const getVersionComparisonAuthor = useCallback(
    (userId?: string | null): SimpleEditorVersionComparisonAuthor => {
      const fallbackId = getVersionComparisonAuthorId(userId);

      return (
        versionComparisonAuthorsRef.current.get(fallbackId) ??
        versionComparisonUserMap.get(fallbackId) ?? {
          id: fallbackId,
          name:
            fallbackId === UNKNOWN_VERSION_COMPARISON_USER_ID
              ? "Unknown user"
              : `User ${fallbackId.slice(0, 8)}`,
          color: getVersionComparisonColorForUserId(fallbackId),
        }
      );
    },
    [versionComparisonUserMap]
  );

  const setActiveVersionComparisonAuthors = useCallback(
    (authors: SimpleEditorVersionComparisonAuthor[]) => {
      versionComparisonAuthorsRef.current = new Map(
        authors.map((author) => [author.id, author])
      );
    },
    []
  );

  const getVersionComparisonAuthors = useCallback(
    (authorIds: Array<string | null | undefined>) =>
      getVersionComparisonAuthorsForIds({
        authorIds,
        orgUsers,
        trackedChangesUserId,
        trackedChangesUserName,
      }),
    [orgUsers, trackedChangesUserId, trackedChangesUserName]
  );

  const hideComparisonPreview = useCallback(() => {
    comparisonRequestIdRef.current += 1;
    hideDiffIfAvailable(previewEditorRef.current);
    hideDiffIfAvailable(readyEditor);
    setActiveVersionComparisonAuthors([]);
    setVersionComparisonStatus({ status: "idle" });
  }, [previewEditorRef, readyEditor, setActiveVersionComparisonAuthors]);

  const isVersionComparisonLoading =
    versionComparisonStatus.status === "loading";
  const versionComparison =
    versionComparisonStatus.status === "ready"
      ? versionComparisonStatus.comparison
      : null;
  const comparison =
    versionComparisonStatus.status === "idle"
      ? null
      : {
          onExit: hideComparisonPreview,
          sourceTitle:
            versionComparisonStatus.status === "loading"
              ? versionComparisonStatus.fromTitle
              : versionComparisonStatus.comparison.fromTitle,
          sourceVersion:
            versionComparisonStatus.status === "loading"
              ? versionComparisonStatus.fromVersion
              : versionComparisonStatus.comparison.fromVersion,
          targetTitle: versionComparisonStatus.toTitle,
          targetVersion: versionComparisonStatus.toVersion,
        };

  const compareVersion = useCallback(
    (
      version: SimpleEditorSnapshotVersion,
      targetVersion?: SimpleEditorSnapshotVersion
    ) => {
      const currentEditor = getReadyEditor(readyEditor);
      const currentPreviewEditor = getReadyEditor(previewEditorRef.current);

      if (
        !collaborationProvider ||
        !currentEditor ||
        !currentPreviewEditor ||
        latestTiptapVersion === null
      ) {
        return;
      }

      const orderedVersions =
        targetVersion && targetVersion.version < version.version
          ? { earlier: targetVersion, later: version }
          : { earlier: version, later: targetVersion };
      const { earlier: earlierVersion, later: laterSavedVersion } =
        orderedVersions;
      const sourceTitle = getVersionTitleForDisplay(version);
      const targetTitle = targetVersion
        ? getVersionTitleForDisplay(targetVersion)
        : null;

      hideComparisonPreview();
      onSelectVersion(version.version);
      setVersionComparisonStatus({
        fromTitle: sourceTitle,
        fromVersion: version.version,
        status: "loading",
        toTitle: targetTitle,
        toVersion: targetVersion?.version ?? null,
      });

      const comparisonRequestId = comparisonRequestIdRef.current;
      const targetVersionPreview = laterSavedVersion
        ? fetchSnapshotVersionPreview({
            provider: collaborationProvider,
            version: laterSavedVersion.version,
          })
        : Promise.resolve({
            content: currentEditor.getJSON(),
            snapshot: Y.encodeStateAsUpdateV2(collaborationProvider.document),
          });

      void Promise.all([
        fetchSnapshotVersionPreview({
          provider: collaborationProvider,
          version: earlierVersion.version,
        }),
        targetVersionPreview,
      ])
        .then(async ([sourceVersionPreview, resolvedTargetVersionPreview]) => {
          if (comparisonRequestIdRef.current !== comparisonRequestId) {
            return;
          }

          const comparisonEditor = getReadyEditor(previewEditorRef.current);

          if (!comparisonEditor) {
            hideComparisonPreview();
            return;
          }

          const comparedContent = sourceVersionPreview.content
            ? clonePreviewContent(sourceVersionPreview.content)
            : null;
          const currentContent = resolvedTargetVersionPreview.content
            ? clonePreviewContent(resolvedTargetVersionPreview.content)
            : null;
          const { snapshot: prevSnapshot } = sourceVersionPreview;
          const { snapshot } = resolvedTargetVersionPreview;
          const requestedSourceContent =
            version.version === earlierVersion.version
              ? comparedContent
              : currentContent;

          if (requestedSourceContent) {
            onPreviewComparedVersion(version.version, requestedSourceContent);
          }

          // Dynamic atoms store their visible values outside Yjs. Resolve both
          // versions in one authored Yjs history, then give those updates
          // directly to native Snapshot Compare. If any dynamic-content input
          // is unavailable, native comparison still receives the raw snapshots
          // without introducing a second diff source.
          const nativeDynamicComparison = resolveDynamicContentForVersion
            ? await buildDynamicContentVersionComparison({
                earlier: {
                  fromVersion: earlierVersion.version,
                  snapshot: prevSnapshot,
                },
                later: {
                  snapshot,
                  toVersion: laterSavedVersion?.version ?? null,
                },
                latestTiptapVersion,
                resolveDynamicContentForVersion,
                schema: comparisonEditor.schema,
              })
            : null;

          if (comparisonRequestIdRef.current !== comparisonRequestId) {
            return;
          }

          const showVersionComparison = ({
            diffSet,
            tr,
          }: {
            diffSet: Diff[];
            tr: ChangeTrackingTransform;
          }) => {
            if (comparisonRequestIdRef.current !== comparisonRequestId) {
              return;
            }

            const comparisonDiffs = sanitizeNativeVersionComparisonDiffs({
              diffs: diffSet,
            })
              .filter(shouldRenderVersionComparisonDiff)
              .map(normalizeVersionComparisonDiffForRendering);
            const authorIds = Array.from(
              new Set(
                comparisonDiffs
                  .filter((diff) =>
                    shouldShowVersionComparisonDiffInLegend({
                      decorationDiffType:
                        getVersionComparisonDecorationDiffType(diff),
                      type: diff.type,
                    })
                  )
                  .map((diff) => diff.attribution.userId ?? undefined)
              )
            );
            const authors = getVersionComparisonAuthors(authorIds);
            const comparisonState: SimpleEditorVersionComparisonState = {
              fromVersion: version.version,
              fromTitle: sourceTitle,
              authors,
            };

            setActiveVersionComparisonAuthors(authors);
            const didShowDiff = comparisonEditor.commands.showDiff(tr, {
              diffs: comparisonDiffs,
            });

            if (!didShowDiff) {
              hideComparisonPreview();
              return false;
            }

            setVersionComparisonStatus({
              comparison: comparisonState,
              status: "ready",
              toTitle: targetTitle,
              toVersion: targetVersion?.version ?? null,
            });
            return true;
          };
          const didStartComparison = comparisonEditor.commands.compareVersions({
            prevSnapshot: nativeDynamicComparison?.prevSnapshot ?? prevSnapshot,
            snapshot: nativeDynamicComparison?.snapshot ?? snapshot,
            hydrateUserData: ({ type, userId }) => {
              const author = getVersionComparisonAuthor(userId);
              const backgroundColor =
                type === "removed"
                  ? getVersionComparisonRemovedColor(author.color)
                  : author.color;

              return {
                userName: author.name,
                color: {
                  backgroundColor,
                },
              };
            },
            onCompare: (context) => {
              if (comparisonRequestIdRef.current !== comparisonRequestId) {
                return;
              }

              if ("error" in context) {
                console.error(
                  "Unable to compare Tiptap versions",
                  context.error
                );
                hideComparisonPreview();
                return;
              }

              showVersionComparison(context);
            },
          });

          if (!didStartComparison) {
            hideComparisonPreview();
          }
        })
        .catch((error) => {
          if (comparisonRequestIdRef.current !== comparisonRequestId) {
            return;
          }

          console.error("Unable to preview Tiptap versions", error);
          hideComparisonPreview();
        });
    },
    [
      collaborationProvider,
      getVersionComparisonAuthor,
      getVersionComparisonAuthors,
      getVersionTitleForDisplay,
      hideComparisonPreview,
      latestTiptapVersion,
      onPreviewComparedVersion,
      onSelectVersion,
      previewEditorRef,
      resolveDynamicContentForVersion,
      setActiveVersionComparisonAuthors,
      readyEditor,
    ]
  );

  return {
    compareVersion,
    comparison,
    getVersionComparisonAuthor,
    hideComparisonPreview,
    isVersionComparisonLoading,
    versionComparison,
  };
};
export const useSimpleEditorVersions = ({
  collaborationProvider,
  getVersionCardAuthorByVersion,
  getVersionCardRestorableByVersion,
  getVersionCardStatusByVersion,
  getVersionCardTitleByVersion,
  isEnabled = true,
  isEditable,
  onRestoreVersion,
  orgUsers,
  readyEditor,
  resolveDynamicContentForVersion,
  trackedChangesUserId,
  trackedChangesUserName,
  versionsRefreshKey,
}: UseSimpleEditorVersionsOptions) => {
  const [versions, setVersions] = useState<SimpleEditorSnapshotVersion[]>([]);
  const [isVersionHistorySynced, setIsVersionHistorySynced] = useState(false);
  const [selectedVersion, setSelectedVersionState] = useState<number | null>(
    null
  );
  const [previewedVersion, setPreviewedVersion] = useState<number | null>(null);
  const [isVersionPreviewLoading, setIsVersionPreviewLoading] = useState(false);
  const [versionCardFilter, setVersionCardFilter] =
    useState<VersionCardFilter>("relevant");
  const [headContent, setHeadContent] = useState<JSONContent | null>(null);
  const [latestVersionPreview, setLatestVersionPreview] =
    useState<LatestVersionPreview | null>(null);
  const selectedVersionRef = useRef<number | null>(selectedVersion);
  const previewEditorRef = useRef<Editor | null>(null);
  const previewedVersionRef = useRef<number | null>(null);
  const hasRequestedVersionsRef = useRef(false);
  const latestVersionPreviewRequestIdRef = useRef(0);
  const previewContentCacheRef = useRef(new Map<number, JSONContent>());
  const currentVersion = getLatestSnapshotVersion(versions);
  const headMatchingVersion = getHeadMatchingVersion({
    currentVersion,
    headContent,
    latestVersionPreview,
  });

  useEffect(() => {
    hasRequestedVersionsRef.current = false;
    previewContentCacheRef.current.clear();
    setLatestVersionPreview(null);
    setIsVersionHistorySynced(false);
    setVersionCardFilter("relevant");
  }, [versionsRefreshKey]);

  useEffect(() => {
    const currentEditor = getReadyEditor(readyEditor);

    if (!currentEditor) {
      setHeadContent(null);
      return;
    }

    const updateHeadContent = () => {
      const nextHeadContent = currentEditor.getJSON();

      setHeadContent((previousHeadContent) =>
        isEqual(previousHeadContent, nextHeadContent)
          ? previousHeadContent
          : nextHeadContent
      );
    };

    updateHeadContent();
    currentEditor.on("update", updateHeadContent);

    return () => {
      currentEditor.off("update", updateHeadContent);
    };
  }, [readyEditor, versionsRefreshKey]);

  useEffect(() => {
    const requestId = latestVersionPreviewRequestIdRef.current + 1;
    latestVersionPreviewRequestIdRef.current = requestId;

    if (!collaborationProvider || currentVersion === null) {
      setLatestVersionPreview(null);
      return;
    }

    const cachedContent = previewContentCacheRef.current.get(currentVersion);
    if (cachedContent) {
      setLatestVersionPreview({
        content: cachedContent,
        version: currentVersion,
      });
      return;
    }

    setLatestVersionPreview(null);
    void fetchSnapshotVersionPreview({
      provider: collaborationProvider,
      version: currentVersion,
    })
      .then(({ content }) => {
        if (
          latestVersionPreviewRequestIdRef.current !== requestId ||
          !content
        ) {
          return;
        }

        const cachedPreviewContent = clonePreviewContent(content);
        previewContentCacheRef.current.set(
          currentVersion,
          cachedPreviewContent
        );
        setLatestVersionPreview({
          content: cachedPreviewContent,
          version: currentVersion,
        });
      })
      .catch((error) => {
        if (latestVersionPreviewRequestIdRef.current !== requestId) {
          return;
        }

        console.error("Unable to inspect the newest Tiptap version", error);
        setLatestVersionPreview(null);
      });
  }, [collaborationProvider, currentVersion, versionsRefreshKey]);
  const versionCardStatusByVersion = useMemo(
    () =>
      getVersionCardStatusByVersion?.(currentVersion) ??
      EMPTY_VERSION_CARD_STATUS_BY_VERSION,
    [currentVersion, getVersionCardStatusByVersion]
  );
  const versionCardAuthorByVersion = useMemo(
    () =>
      getVersionCardAuthorByVersion?.(currentVersion) ??
      (new Map() satisfies SimpleEditorVersionCardAuthorByVersion),
    [currentVersion, getVersionCardAuthorByVersion]
  );
  const versionCardRestorableByVersion = useMemo(
    () =>
      getVersionCardRestorableByVersion?.(currentVersion) ??
      EMPTY_VERSION_CARD_RESTORABLE_BY_VERSION,
    [currentVersion, getVersionCardRestorableByVersion]
  );
  const versionCardTitleByVersion = useMemo(
    () =>
      getVersionCardTitleByVersion?.(currentVersion) ??
      EMPTY_VERSION_CARD_TITLE_BY_VERSION,
    [currentVersion, getVersionCardTitleByVersion]
  );
  const getDisplayVersionTitle = useCallback(
    (version: SimpleEditorSnapshotVersion) =>
      versionCardTitleByVersion.get(version.version) ??
      getVersionTitle(version),
    [versionCardTitleByVersion]
  );
  const getIsVersionRestorable = useCallback(
    (version: number) => versionCardRestorableByVersion.get(version) !== false,
    [versionCardRestorableByVersion]
  );

  const setSelectedVersion = useCallback(
    (nextSelectedVersion: number | null) => {
      selectedVersionRef.current = nextSelectedVersion;
      setSelectedVersionState(nextSelectedVersion);
    },
    []
  );
  const closeVersionPreview = useCallback(() => {
    previewedVersionRef.current = null;
    setPreviewedVersion(null);
    setIsVersionPreviewLoading(false);
    getReadyEditor(previewEditorRef.current)?.commands.clearContent();
  }, []);

  const previewComparedVersion = useCallback(
    (version: number, nextContent: JSONContent) => {
      const previewContent = clonePreviewContent(nextContent);

      previewedVersionRef.current = version;
      previewContentCacheRef.current.set(version, previewContent);
      setPreviewedVersion(version);
      setIsVersionPreviewLoading(false);
      getReadyEditor(previewEditorRef.current)?.commands.setContent(
        previewContent
      );
    },
    []
  );

  const {
    compareVersion,
    comparison: versionComparisonAction,
    getVersionComparisonAuthor,
    hideComparisonPreview,
    isVersionComparisonLoading,
    versionComparison,
  } = useSimpleEditorVersionComparison({
    collaborationProvider,
    latestTiptapVersion: currentVersion,
    getVersionTitleForDisplay: getDisplayVersionTitle,
    onPreviewComparedVersion: previewComparedVersion,
    onSelectVersion: setSelectedVersion,
    orgUsers,
    previewEditorRef,
    readyEditor,
    resolveDynamicContentForVersion,
    trackedChangesUserId,
    trackedChangesUserName,
  });
  const isVersionComparisonActive = Boolean(versionComparisonAction);
  const clearSelectedVersion = useCallback(() => {
    setSelectedVersion(null);
    closeVersionPreview();
    hideComparisonPreview();
  }, [closeVersionPreview, hideComparisonPreview, setSelectedVersion]);
  const comparison =
    versionComparisonAction === null
      ? null
      : { ...versionComparisonAction, onExit: clearSelectedVersion };
  const getVersionComparisonAuthorRef = useRef(getVersionComparisonAuthor);
  getVersionComparisonAuthorRef.current = getVersionComparisonAuthor;

  const previewEditor = useEditor(
    {
      immediatelyRender: false,
      editable: false,
      editorProps: {
        attributes: {
          "aria-label": "Version preview",
          class: "simple-editor simple-editor--preview",
        },
      },
      extensions: createVersionPreviewExtensions({
        collaborationProvider,
        getVersionComparisonAuthorRef,
      }),
    },
    [collaborationProvider]
  );

  previewEditorRef.current = previewEditor;
  selectedVersionRef.current = selectedVersion;

  const handleSnapshotUpdate = useCallback(
    (nextVersions: SimpleEditorSnapshotVersion[]) => {
      if (!hasRequestedVersionsRef.current && nextVersions.length === 0) {
        return;
      }

      const currentSelectedVersion = selectedVersionRef.current;
      const hasCurrentSelection =
        currentSelectedVersion !== null &&
        nextVersions.some(
          (version) => version.version === currentSelectedVersion
        );

      setVersions(nextVersions);
      setIsVersionHistorySynced(true);

      if (currentSelectedVersion !== null && !hasCurrentSelection) {
        setSelectedVersion(null);
      }
    },
    [setSelectedVersion]
  );

  const previewedVersionData = useMemo(
    () =>
      versions.find((version) => version.version === previewedVersion) ?? null,
    [previewedVersion, versions]
  );
  const previewViewingMessage = previewedVersionData
    ? getVersionViewingMessage({
        date: previewedVersionData.date,
        revisionTitle: versionCardTitleByVersion.get(
          previewedVersionData.version
        ),
        status:
          versionCardStatusByVersion.get(previewedVersionData.version) ?? null,
      })
    : null;
  const isVersionContentPreviewActive =
    previewedVersion !== null || isVersionPreviewLoading;
  const isVersionPreviewActive =
    isVersionContentPreviewActive || isVersionComparisonActive;
  const isLoading =
    (isEnabled &&
      (!collaborationProvider ||
        !getReadyEditor(readyEditor) ||
        !isVersionHistorySynced)) ||
    isVersionPreviewLoading ||
    isVersionComparisonLoading;

  const previewAction = useMemo<SimpleEditorPreviewAction | null>(() => {
    if (!previewViewingMessage && !isVersionPreviewLoading) {
      return null;
    }

    return {
      ariaLabel: "Exit version viewing",
      kind: "close",
      label: "Exit",
      onClick: clearSelectedVersion,
      viewingMessage: previewViewingMessage ?? "Viewing: Saved Version",
    };
  }, [clearSelectedVersion, isVersionPreviewLoading, previewViewingMessage]);

  const previewVersion = useCallback(
    (version: number) => {
      setSelectedVersion(version);

      const currentPreviewEditor = getReadyEditor(previewEditorRef.current);

      if (!collaborationProvider || !currentPreviewEditor) {
        return;
      }

      previewedVersionRef.current = version;
      setPreviewedVersion(version);

      const cachedPreviewContent = previewContentCacheRef.current.get(version);
      if (cachedPreviewContent) {
        currentPreviewEditor.commands.setContent(
          clonePreviewContent(cachedPreviewContent)
        );
        setIsVersionPreviewLoading(false);
        return;
      }

      setIsVersionPreviewLoading(true);
      currentPreviewEditor.commands.clearContent();
      collaborationProvider.previewVersion(version);
    },
    [collaborationProvider, setSelectedVersion]
  );

  const compareVersionByNumber = useCallback(
    (version: number) => {
      // Route synchronization writes the active source back to the URL. Ignore
      // that echo so it cannot replace a saved target with Current.
      if (version === comparison?.sourceVersion) {
        return;
      }

      const snapshotVersion = versions.find(
        (candidate) => candidate.version === version
      );

      if (snapshotVersion) {
        compareVersion(snapshotVersion);
      }
    },
    [compareVersion, comparison?.sourceVersion, versions]
  );

  const handleSelectVersion = useCallback(
    (version: number) => {
      if (isVersionComparisonActive) {
        compareVersionByNumber(version);
        return;
      }

      previewVersion(version);
    },
    [compareVersionByNumber, isVersionComparisonActive, previewVersion]
  );

  useEffect(() => {
    if (
      previewedVersion !== null &&
      !versions.some((version) => version.version === previewedVersion)
    ) {
      closeVersionPreview();
    }
  }, [closeVersionPreview, previewedVersion, versions]);

  useEffect(() => {
    if (previewedVersion !== null && selectedVersion !== previewedVersion) {
      closeVersionPreview();
    }
  }, [closeVersionPreview, previewedVersion, selectedVersion]);

  useEffect(() => {
    const currentEditor = getReadyEditor(readyEditor);

    if (!currentEditor || !collaborationProvider) {
      hasRequestedVersionsRef.current = false;
      setVersions([]);
      setIsVersionHistorySynced(false);
      setSelectedVersion(null);
      previewedVersionRef.current = null;
      setPreviewedVersion(null);
      setIsVersionPreviewLoading(false);
      previewContentCacheRef.current.clear();
      hideComparisonPreview();
      getReadyEditor(previewEditorRef.current)?.commands.clearContent();
      return;
    }

    const initializeVersionHistory = () => {
      const currentEditor = getReadyEditor(readyEditor);

      if (!currentEditor) {
        return;
      }

      hasRequestedVersionsRef.current = true;
      configureSnapshotVersioning({
        editor: currentEditor,
        isEditable,
        provider: collaborationProvider,
      });
    };

    collaborationProvider.on("synced", initializeVersionHistory);

    if (collaborationProvider.isSynced) {
      initializeVersionHistory();
    }

    return () => {
      collaborationProvider.off("synced", initializeVersionHistory);
    };
  }, [
    collaborationProvider,
    hideComparisonPreview,
    isEditable,
    readyEditor,
    setSelectedVersion,
  ]);

  useEffect(() => {
    const currentEditor = getReadyEditor(readyEditor);

    if (!currentEditor || !collaborationProvider?.isSynced) {
      return;
    }

    hasRequestedVersionsRef.current = true;
    currentEditor.commands.fetchVersions();
  }, [collaborationProvider, readyEditor, versionsRefreshKey]);

  useEffect(() => {
    if (!collaborationProvider || !previewEditor) {
      return;
    }

    const handleStateless = (event: { payload: string }) => {
      if (isVersionComparisonActive) {
        return;
      }

      const payload = parseVersionPreviewPayload(event.payload);
      const requestedVersion = previewedVersionRef.current;

      if (
        !payload ||
        requestedVersion === null ||
        payload.version !== requestedVersion
      ) {
        return;
      }

      const content = getVersionPreviewContentFromPayload(event.payload);
      const currentPreviewEditor = getReadyEditor(previewEditorRef.current);

      if (!content || !currentPreviewEditor) {
        return;
      }

      const cachedPreviewContent = clonePreviewContent(content);
      previewContentCacheRef.current.set(
        requestedVersion,
        cachedPreviewContent
      );
      currentPreviewEditor.commands.setContent(cachedPreviewContent);
      setIsVersionPreviewLoading(false);
    };

    collaborationProvider.on("stateless", handleStateless);

    return () => {
      collaborationProvider.off("stateless", handleStateless);
    };
  }, [collaborationProvider, isVersionComparisonActive, previewEditor]);

  const restoreVersion = useCallback(
    async (version: SimpleEditorSnapshotVersion) => {
      const currentEditor = getReadyEditor(readyEditor);

      if (!currentEditor) {
        return;
      }

      if (!getIsVersionRestorable(version.version)) {
        return;
      }

      const versionTitle = getDisplayVersionTitle(version);
      const accepted = window.confirm(
        `Restore "${versionTitle}"? Unsaved changes will be versioned before the restore.`
      );

      if (!accepted) {
        return;
      }

      if (isVersionComparisonActive) {
        hideComparisonPreview();
      }

      closeVersionPreview();

      const didRestore = currentEditor.commands.revertToVersion(
        version.version,
        `Restore ${versionTitle}`,
        `Before restore to ${versionTitle}`
      );

      if (!didRestore) {
        return;
      }

      setSelectedVersion(null);
      await onRestoreVersion?.(version);
    },
    [
      closeVersionPreview,
      getDisplayVersionTitle,
      getIsVersionRestorable,
      hideComparisonPreview,
      isVersionComparisonActive,
      onRestoreVersion,
      readyEditor,
      setSelectedVersion,
    ]
  );

  const versionCards = useMemo(
    () => (
      <VersionCards
        versions={versions}
        comparison={comparison}
        filter={versionCardFilter}
        headMatchingVersion={headMatchingVersion}
        selectedVersion={selectedVersion}
        versionCardAuthorByVersion={versionCardAuthorByVersion}
        versionCardStatusByVersion={versionCardStatusByVersion}
        versionCardRestorableByVersion={versionCardRestorableByVersion}
        versionCardTitleByVersion={versionCardTitleByVersion}
        users={orgUsers}
        onSelectVersion={handleSelectVersion}
        onCompareVersion={compareVersion}
        onFilterChange={setVersionCardFilter}
        onRestoreVersion={restoreVersion}
      />
    ),
    [
      compareVersion,
      comparison,
      handleSelectVersion,
      headMatchingVersion,
      orgUsers,
      restoreVersion,
      selectedVersion,
      versionCardAuthorByVersion,
      versionCardRestorableByVersion,
      versionCardStatusByVersion,
      versionCardTitleByVersion,
      versionCardFilter,
      versions,
    ]
  );

  return {
    clearSelectedVersion,
    compareVersion: compareVersionByNumber,
    comparison,
    currentVersion,
    handleSnapshotUpdate,
    isVersionHistorySynced,
    isLoading,
    isVersionPreviewActive,
    previewAction,
    previewEditor,
    selectVersion: handleSelectVersion,
    selectedVersion,
    versions,
    versionCards,
    versionComparison,
  };
};
